var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useAddCampaignFormContext, } from 'root/src/client/v2/_pages/add-campaign/contexts/addCampaignForm.context';
import { useQueryClient } from '@tanstack/react-query';
import { GET_DROP_EVENTS, } from 'root/src/shared/descriptions/endpoints/endpointIds';
import { useRouter } from 'next/router';
import { formatMnemonicId } from 'root/src/client/v2/_pages/add-campaign/helpers/formatter';
import { DareDropApiError } from 'root/src/client/v2/common/api/apiError';
import { isPayloadSchemaError, } from 'root/src/shared/error/guards/isPayloadSchemaError.guard';
import toast from 'react-hot-toast';
import analyticsSendItem from 'root/src/client/logic/analytics/util/analyticsSendItem';
import { useUserId } from 'root/src/client/v2/auth/hooks/useUserId';
import { DROPS_ANALYTIC_EVENTS } from 'root/src/shared/drops/drops.analytics';
import { useSaveDropEventMutation } from 'root/src/client/v2/drop/hooks/useDropEvent';
const REFETCH_DROP_EVENTS_TIME = 0;
export function useAddCampaignFormSubmits() {
    const form = useAddCampaignFormContext();
    const queryClient = useQueryClient();
    const saveDropEventMutation = useSaveDropEventMutation();
    const router = useRouter();
    const campaignName = form.watch('campaignName');
    const id = form.watch('id');
    const { userId } = useUserId();
    const isPreviewAvailable = Boolean(id && campaignName);
    const onSubmit = form.handleSubmit((data) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        try {
            const response = yield saveDropEventMutation.mutateAsync(data);
            // set id to prevent creating new event
            form.setValue('id', response.id, {
                shouldDirty: false,
            });
            // update brandId
            form.setValue('brandId', (_a = response.brand) === null || _a === void 0 ? void 0 : _a.id, {
                shouldDirty: false,
            });
            // update assets to prevent re-optimization of assets
            form.setValue('assets', response.assets, {
                shouldDirty: false,
            });
            // update dares to set ids
            form.setValue('dares', response.dares, {
                shouldDirty: false,
            });
            // update brandLogo
            form.setValue('brandLogoUrl', (_c = (_b = response.brand) === null || _b === void 0 ? void 0 : _b.brandLogo) === null || _c === void 0 ? void 0 : _c.big, {
                shouldDirty: false,
            });
            setTimeout(() => {
                void queryClient.refetchQueries({
                    queryKey: [GET_DROP_EVENTS],
                });
            }, REFETCH_DROP_EVENTS_TIME);
        }
        catch (err) {
            console.error(err);
            if (err instanceof DareDropApiError) {
                if (isDropSoftError(err.cause)) {
                    form.setValue('id', err.cause.props.dropEvent.id, {
                        shouldDirty: false,
                    });
                    // update brandId
                    form.setValue('brandId', (_d = err.cause.props.dropEvent.brand) === null || _d === void 0 ? void 0 : _d.id, {
                        shouldDirty: false,
                    });
                    // update assets to prevent re-optimization of assets
                    form.setValue('assets', err.cause.props.dropEvent.assets, {
                        shouldDirty: false,
                    });
                    // update dares to set ids
                    form.setValue('dares', err.cause.props.dropEvent.dares, {
                        shouldDirty: false,
                    });
                    // update brandLogo
                    form.setValue('brandLogoUrl', err.cause.props.dropEvent.brandLogoUrl, {
                        shouldDirty: false,
                    });
                    toast.error(err.message);
                    return;
                }
                if (isDropPayloadSchemaError(err.cause)) {
                    for (const [key, message] of Object.entries(err.cause.message.dropEventPayload)) {
                        form.setError(key, {
                            type: 'manual',
                            message: message || 'Incorrect value',
                        });
                    }
                    throw new Error('Form contains errors');
                }
            }
            throw new Error(err instanceof Error ? err.message : 'Failed to save campaign');
        }
    }), (err) => {
        console.error(err);
    });
    const onFormSubmit = (event) => __awaiter(this, void 0, void 0, function* () {
        try {
            analyticsSendItem(DROPS_ANALYTIC_EVENTS.SAVE_DROP, userId);
            yield onSubmit(event);
        }
        catch (err) {
            if (err instanceof Error) {
                toast.error(err.message, {
                    position: 'bottom-center',
                    duration: 10000,
                });
            }
        }
    });
    const onPreview = () => __awaiter(this, void 0, void 0, function* () {
        analyticsSendItem(DROPS_ANALYTIC_EVENTS.PREVIEW_DROP, userId);
        yield onSubmit();
        if (!isPreviewAvailable) {
            return;
        }
        const formattedMnemonicId = formatMnemonicId(campaignName);
        const formData = form.getValues();
        if (!formattedMnemonicId || !formData.id) {
            toast.error('Failed to preview campaign, please save campaign first');
        }
        yield router.push(`/drop-preview/${formattedMnemonicId}?dropId=${formData.id}`);
    });
    return {
        onSubmit,
        onPreview,
        isPreviewAvailable,
        onFormSubmit,
    };
}
function isDropSoftError(error) {
    if (isPayloadSchemaError(error)) {
        return Boolean(typeof error === 'object'
            && 'props' in error
            && error.props
            && typeof error.props === 'object'
            && 'dropEvent' in error.props
            && typeof error.props.dropEvent === 'object');
    }
    return false;
}
function isDropPayloadSchemaError(error) {
    if (isPayloadSchemaError(error)) {
        return typeof error.message === 'object' && 'dropEventPayload' in error.message;
    }
    return false;
}
